import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  Check,
  Search,
  ChevronLeft,
  ShoppingCart,
  Menu,
  LayoutGrid,
  List,
  SlidersHorizontal,
  ChevronDown,
  Globe,
  Home,
  ClipboardList,
  User,
  Plus,
} from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

const BRAND_COLORS = [
  { id: "red", hex: "#DC2626" },
  { id: "blue", hex: "#2563EB" },
  { id: "green", hex: "#0D9488" },
  { id: "purple", hex: "#7C3AED" },
  { id: "navy", hex: "#1E293B" },
  { id: "orange", hex: "#EA580C" },
] as const;

type BrandColorId = (typeof BRAND_COLORS)[number]["id"];

type PreviewLang = "en" | "ar" | "fr" | "es" | "hi" | "zh";

const PREVIEW_LANGUAGES: {
  id: PreviewLang;
  flag: string;
  label: string;
}[] = [
  { id: "en", flag: "🇺🇸", label: "English" },
  { id: "ar", flag: "🇦🇪", label: "العربية" },
  { id: "fr", flag: "🇫🇷", label: "Français" },
  { id: "es", flag: "🇪🇸", label: "Español" },
  { id: "hi", flag: "🇮🇳", label: "हिन्दी" },
  { id: "zh", flag: "🇨🇳", label: "中文" },
];

const ITEM_EMOJIS = ["🍖", "🍔"] as const;

const MOCKUP_CONTENT: Record<
  PreviewLang,
  {
    dir: "ltr" | "rtl";
    restaurantName: string;
    branch: string;
    category: string;
    searchPlaceholder: string;
    chips: string[];
    items: {
      name: string;
      description: string;
      price: string;
      originalPrice?: string;
    }[];
  }
> = {
  en: {
    dir: "ltr",
    restaurantName: "ACME Restaurant",
    branch: "Main Branch",
    category: "Main Course",
    searchPlaceholder: "Search",
    chips: ["Main Course", "Sides & Drinks", "Specials"],
    items: [
      {
        name: "BBQ Platter",
        description:
          "Smoky half-rack, slow-cooked, brushed with house sauce...",
        price: "50",
      },
      {
        name: "Smash Burger",
        description:
          "Double patty, melted cheese, pickles, and secret sauce...",
        price: "28",
        originalPrice: "32",
      },
    ],
  },
  ar: {
    dir: "rtl",
    restaurantName: "مطعم ACME",
    branch: "الفرع الرئيسي",
    category: "الأطباق الرئيسية",
    searchPlaceholder: "بحث",
    chips: ["الأطباق الرئيسية", "المقبلات والمشروبات", "العروض"],
    items: [
      {
        name: "طبق مشاوي",
        description: "نصف ضلوع مدخن، مطبوخ ببطء، مع صلصة البيت الخاصة...",
        price: "٥٠",
      },
      {
        name: "برجر سماش",
        description: "طبقتان من اللحم، جبنة ذائبة، مخلل وصلصة سرية...",
        price: "٢٨",
        originalPrice: "٣٢",
      },
    ],
  },
  fr: {
    dir: "ltr",
    restaurantName: "Restaurant ACME",
    branch: "Agence principale",
    category: "Plats principaux",
    searchPlaceholder: "Rechercher",
    chips: ["Plats principaux", "Accompagnements", "Spécialités"],
    items: [
      {
        name: "Plateau BBQ",
        description:
          "Demi-côte fumée, mijotée lentement, nappée de sauce maison...",
        price: "50",
      },
      {
        name: "Smash Burger",
        description:
          "Double steak, fromage fondant, cornichons et sauce secrète...",
        price: "28",
        originalPrice: "32",
      },
    ],
  },
  es: {
    dir: "ltr",
    restaurantName: "Restaurante ACME",
    branch: "Sucursal principal",
    category: "Plato principal",
    searchPlaceholder: "Buscar",
    chips: ["Plato principal", "Acompañamientos", "Especiales"],
    items: [
      {
        name: "Bandeja BBQ",
        description:
          "Media costilla ahumada, cocida a fuego lento, con salsa de la casa...",
        price: "50",
      },
      {
        name: "Smash Burger",
        description:
          "Doble carne, queso fundido, pepinillos y salsa secreta...",
        price: "28",
        originalPrice: "32",
      },
    ],
  },
  hi: {
    dir: "ltr",
    restaurantName: "ACME रेस्टोरेंट",
    branch: "मुख्य शाखा",
    category: "मुख्य व्यंजन",
    searchPlaceholder: "खोजें",
    chips: ["मुख्य व्यंजन", "साइड और पेय", "विशेष"],
    items: [
      {
        name: "बीबीक्यू प्लैटर",
        description: "धुएँदार हाफ-रैक, धीमी आंच पर पका, घर की सॉस के साथ...",
        price: "50",
      },
      {
        name: "स्मैश बर्गर",
        description: "डबल पैटी, पिघला चीज़, अचार और गुप्त सॉस...",
        price: "28",
        originalPrice: "32",
      },
    ],
  },
  zh: {
    dir: "ltr",
    restaurantName: "ACME 餐厅",
    branch: "主分店",
    category: "主菜",
    searchPlaceholder: "搜索",
    chips: ["主菜", "小食和饮品", "特价"],
    items: [
      {
        name: "烧烤拼盘",
        description: "烟熏半肋排，慢火烹制，淋上招牌酱汁...",
        price: "50",
      },
      {
        name: "招牌汉堡",
        description: "双层肉饼、融化芝士、酸黄瓜和秘制酱料...",
        price: "28",
        originalPrice: "32",
      },
    ],
  },
};

function MenuPhoneMockup({
  brandColor,
  language,
}: {
  brandColor: string;
  language: PreviewLang;
}) {
  const content = MOCKUP_CONTENT[language];
  const langMeta = PREVIEW_LANGUAGES.find((l) => l.id === language)!;

  return (
    <div
      className="relative mx-auto w-full max-w-[280px] sm:max-w-[300px]"
      role="img"
      aria-label="Interactive menu preview"
      dir={content.dir}
    >
      <div className="rounded-[2.25rem] border-[6px] border-gray-900 bg-gray-900 p-1.5 shadow-2xl shadow-gray-900/15">
        <div className="relative flex h-[580px] flex-col overflow-hidden rounded-[1.75rem] bg-[#f5f5f5] sm:h-[620px]">
          {/* Top nav */}
          <div className="flex shrink-0 items-center justify-between bg-white px-3 py-2.5">
            <div className="flex min-w-0 items-center gap-1">
              <ChevronLeft
                aria-hidden="true"
                className="h-4 w-4 shrink-0 text-gray-900"
                strokeWidth={2.5}
              />
              <span className="truncate text-[11px] font-bold text-gray-900">
                {content.restaurantName}
              </span>
            </div>
            <div className="flex shrink-0 items-center gap-1.5">
              <div className="flex h-7 w-7 items-center justify-center rounded-full bg-gray-900">
                <ShoppingCart
                  aria-hidden="true"
                  className="h-3.5 w-3.5 text-white"
                  strokeWidth={2}
                />
              </div>
              <Menu
                aria-hidden="true"
                className="h-4 w-4 text-gray-900"
                strokeWidth={2}
              />
            </div>
          </div>

          {/* Hero */}
          <div className="relative h-[130px] shrink-0 overflow-hidden">
            <motion.div
              className="absolute inset-0"
              style={{ backgroundColor: brandColor }}
              animate={{ backgroundColor: brandColor }}
              transition={{ duration: 0.35, ease: "easeOut" }}
              aria-hidden="true"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-white/10 to-black/15" />

            <div className="absolute start-2.5 top-2.5 flex items-center gap-1 rounded-full bg-black/50 px-2 py-0.5 backdrop-blur-sm">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
              <span className="text-[8px] font-semibold text-white">
                {content.branch}
              </span>
            </div>

            <div className="absolute end-2.5 top-2.5 flex items-center gap-0.5 rounded-full bg-black/50 px-2 py-0.5 backdrop-blur-sm">
              <Globe
                aria-hidden="true"
                className="h-2.5 w-2.5 text-white"
                strokeWidth={2}
              />
              <span className="text-[8px] font-semibold text-white">
                {langMeta.label}
              </span>
              <ChevronDown
                aria-hidden="true"
                className="h-2.5 w-2.5 text-white"
                strokeWidth={2.5}
              />
            </div>

            <div className="absolute left-1/2 top-1/2 flex -translate-x-1/2 -translate-y-1/2 items-center gap-1 rounded-xl bg-black/55 px-3 py-1 backdrop-blur-sm">
              <span className="text-sm sm:text-base w-max font-bold text-white">
                {content.category}
              </span>
              <ChevronDown
                aria-hidden="true"
                className="h-3 w-3 text-white"
                strokeWidth={2.5}
              />
            </div>
          </div>

          {/* Search + view toggles */}
          <div className="flex shrink-0 items-center gap-1.5 bg-white px-2.5 py-2">
            <div className="relative min-w-0 flex-1">
              <Search
                aria-hidden="true"
                className="pointer-events-none absolute start-2.5 top-1/2 h-3 w-3 -translate-y-1/2 text-gray-400"
              />
              <div className="rounded-full border border-gray-200 bg-white py-1.5 ps-7 pe-2 text-start text-[9px] text-gray-400">
                {content.searchPlaceholder}
              </div>
            </div>
            <div className="flex shrink-0 gap-1">
              <div className="flex h-7 w-7 items-center justify-center rounded-lg border border-gray-200 bg-white">
                <LayoutGrid
                  aria-hidden="true"
                  className="h-3 w-3 text-gray-500"
                  strokeWidth={2}
                />
              </div>
              <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-gray-900">
                <List
                  aria-hidden="true"
                  className="h-3 w-3 text-white"
                  strokeWidth={2}
                />
              </div>
            </div>
          </div>

          {/* Category chips */}
          <div className="flex shrink-0 items-center gap-1.5 overflow-hidden bg-white px-2.5 pb-2">
            {content.chips.map((chip, i) => (
              <motion.span
                key={chip}
                className={`shrink-0 rounded-full px-2.5 py-1 text-[8px] font-semibold ${
                  i === 0
                    ? "text-white"
                    : "border border-gray-200 bg-white text-gray-700"
                }`}
                style={i === 0 ? { backgroundColor: brandColor } : undefined}
                animate={i === 0 ? { backgroundColor: brandColor } : undefined}
                transition={{ duration: 0.35, ease: "easeOut" }}
              >
                {chip}
              </motion.span>
            ))}
            <div className="ms-auto flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-gray-200 bg-white">
              <SlidersHorizontal
                aria-hidden="true"
                className="h-2.5 w-2.5 text-gray-600"
                strokeWidth={2}
              />
            </div>
          </div>

          {/* Scrollable menu body */}
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden px-2.5 pb-12">
            {/* Category banner */}
            <span className="text-base font-bold text-gray-900 py-2">
              {content.category}
            </span>

            {/* Product cards */}
            <div className="space-y-2 overflow-hidden">
              {content.items.map((item, index) => (
                <div
                  key={item.name}
                  className="flex gap-2 rounded-xl bg-white p-2 shadow-sm"
                >
                  <div className="min-w-0 flex-1 text-start">
                    <div className="text-[10px] font-bold leading-tight text-gray-900">
                      {item.name}
                    </div>
                    <div className="mt-0.5 line-clamp-2 text-[8px] leading-snug text-gray-400">
                      {item.description}
                    </div>
                    <div className="mt-1 flex items-center gap-1">
                      <span className="text-[8px] font-bold text-gray-500">
                        $
                      </span>
                      <span className="text-[9px] font-bold text-gray-900">
                        {item.price}
                      </span>
                      {item.originalPrice && (
                        <>
                          <span className="text-[8px] text-gray-400">$</span>
                          <span className="text-[8px] text-gray-400 line-through">
                            {item.originalPrice}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="relative flex h-14 w-14 shrink-0 items-center justify-center rounded-lg bg-[#FFF4EB] text-2xl">
                    <span aria-hidden="true">{ITEM_EMOJIS[index]}</span>
                    <motion.div
                      className="absolute -bottom-0.5 -end-0.5 flex h-5 w-5 items-center justify-center rounded-full shadow-sm"
                      style={{ backgroundColor: brandColor }}
                      animate={{ backgroundColor: brandColor }}
                      transition={{ duration: 0.35, ease: "easeOut" }}
                    >
                      <Plus
                        aria-hidden="true"
                        className="h-3 w-3 text-white"
                        strokeWidth={3}
                      />
                    </motion.div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Bottom nav */}
          <div className="absolute inset-x-2.5 bottom-2.5 flex items-center justify-around rounded-2xl border border-white/60 bg-white/75 px-4 py-2.5 shadow-lg backdrop-blur-md">
            <Home
              aria-hidden="true"
              className="h-4 w-4 text-gray-800"
              strokeWidth={2}
            />
            <ClipboardList
              aria-hidden="true"
              className="h-4 w-4 text-gray-400"
              strokeWidth={2}
            />
            <User
              aria-hidden="true"
              className="h-4 w-4 text-gray-400"
              strokeWidth={2}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default function MenoBrandLook() {
  const { t, isRTL } = useLanguage();
  const textAlign = "text-left";

  const [selectedColor, setSelectedColor] = useState<BrandColorId>("red");
  const [selectedLanguage, setSelectedLanguage] = useState<PreviewLang>("en");

  const activeColor =
    BRAND_COLORS.find((c) => c.id === selectedColor)?.hex ??
    BRAND_COLORS[0].hex;

  const itemReveal = {
    hidden: { opacity: 0, y: 24 },
    show: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.55, ease: "easeOut" },
    },
  };

  return (
    <section className=" py-12 lg:py-16 safari-paint-contain">
      <div className="custom-container">
        <motion.div
          className="overflow-hidden rounded-3xl border border-pink-100/60 bg-[#eee8e1]/50 px-4 py-10 sm:px-6 sm:py-12 lg:px-14 lg:py-16"
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.15 }}
          variants={{
            hidden: { opacity: 0, y: 40 },
            show: {
              opacity: 1,
              y: 0,
              transition: { duration: 0.6, staggerChildren: 0.1 },
            },
          }}
        >
          <div
            className={`flex flex-col items-stretch gap-12 lg:flex-row lg:items-center lg:gap-16 xl:gap-20`}
          >
            <motion.div className={`w-full flex-1 ${textAlign}`} variants={itemReveal}>
              <span
                className={`mb-5 inline-flex items-center rounded-full bg-white px-4 py-1.5 text-xs font-bold tracking-wide text-[#FF00A4]`}
              >
                {t("brandLook.badge")}
              </span>

              <h2 style={{ lineHeight: 0 }}>
                <span
                  className="text-3xl font-extrabold leading-tight text-gray-900 sm:text-4xl lg:text-5xl"
                  style={{
                    fontFamily: '"Inter", ui-sans-serif, system-ui, sans-serif',
                  }}
                >
                  {t("brandLook.title1")}{" "}
                  <span className="font-serif italic text-[#FF00A4]">
                    {t("brandLook.titleAccent")}
                  </span>
                </span>
              </h2>

              <p>
                <span
                  className="text-lg leading-relaxed text-gray-600 mt-4 max-w-none lg:max-w-xl block"
                  style={{
                    fontFamily: '"Inter", ui-sans-serif, system-ui, sans-serif',
                  }}
                >
                  {t("brandLook.desc")}
                </span>
              </p>

              <div className="mt-10 mb-8">
                <p>
                  <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-gray-500 mb-3 block">
                    {t("brandLook.brandColorLabel")}{" "}
                  </span>
                </p>
                <div
                  className="flex flex-wrap gap-3"
                  role="group"
                  aria-label={t("brandLook.brandColorLabel")}
                >
                  {BRAND_COLORS.map((color) => {
                    const isActive = selectedColor === color.id;
                    return (
                      <button
                        key={color.id}
                        type="button"
                        onClick={() => setSelectedColor(color.id)}
                        aria-pressed={isActive}
                        aria-label={color.id}
                        className="relative flex h-11 w-11 items-center justify-center rounded-full border-2 border-white shadow-md transition-transform hover:scale-110 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#FF00A4]"
                        style={{ backgroundColor: color.hex }}
                      >
                        {isActive && (
                          <Check
                            aria-hidden="true"
                            className="h-5 w-5 text-white drop-shadow-sm"
                            strokeWidth={3}
                          />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="mb-8">
                <p>
                  <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-gray-500 mb-3 block">
                    {t("brandLook.menuLanguageLabel")}{" "}
                  </span>
                </p>
                <div
                  className="flex flex-wrap gap-2.5"
                  role="group"
                  aria-label={t("brandLook.menuLanguageLabel")}
                >
                  {PREVIEW_LANGUAGES.map((lang) => {
                    const isActive = selectedLanguage === lang.id;
                    return (
                      <button
                        key={lang.id}
                        type="button"
                        onClick={() => setSelectedLanguage(lang.id)}
                        aria-pressed={isActive}
                        className={`inline-flex items-center gap-2 rounded-full border px-3.5 py-2 text-sm font-semibold transition-all focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#FF00A4] ${
                          isRTL ? "flex-row-reverse" : ""
                        } ${
                          isActive
                            ? "border-[#FF00A4] bg-white text-gray-900 shadow-sm"
                            : "border-gray-200 bg-white/70 text-gray-600 hover:border-gray-300 hover:bg-white"
                        }`}
                      >
                        <span aria-hidden="true">{lang.flag}</span>
                        {lang.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </motion.div>

            <motion.div
              className={`flex w-full shrink-0 justify-center lg:w-[38%] xl:w-[36%] ${
                isRTL ? "lg:justify-start" : "lg:justify-end"
              }`}
              variants={itemReveal}
            >
              <MenuPhoneMockup
                brandColor={activeColor}
                language={selectedLanguage}
              />
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
